import { FC, useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import classNames from "classnames";
import styles from './../../styles/index.module.scss'
import { DocumentType } from "../../../@types/enumsGlobal"
import { MarketingEventsDataProvider } from "../../../Services/DataProviders/MarketingEventsDataProvider";
import { IMarketingEventsViewDTO } from "../../../libs/coreapi-dto/dirs/marketingEvents";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import useGridFilter, { CreateSelectorGridFilter, MarketingEventsDefaultGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import marketingStyles from './styles/MarketingEvents.module.scss'
import tabsStyles from '../../../pages/styles/homePage.module.scss'
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import TabsPanel from "../../../components/tabs/TabsPanel";
import { usePluginContext } from "../../../system/providers/plugin/PluginContextProvider";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DateTime } from "luxon";
import { t } from "i18next";
import { Accordion } from "../../../components/controls/accordion";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import FlexRow from "../../../components/controls/FlexRow";
import MarketingEventCreatorModal from "./MarketingEventCreatorModal";
import { DatePeriodPanelV2 } from "../../../hoc/DatePeriodPanelV2/DatePeriodPanelV2";
import EntityMultiSelect from "../../../components/entityMultiSelect/entityMultiSelect";
import InputSearch from "../../../components/inputSearch/inputSearch";
import { User2ContractorDataProvider } from "../../../Services/DataProviders/User2ContractorDataProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import React from "react";
import { SelectAnt } from "../../../components/selects/selectAnt/selectAnt";
import { ISelectedItems, MarketingEventStateText, MarketingEventTypeText } from "./interfaces";

export const MarketingEventsPluginView: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const userContext = useUserContext();
    const pluginContext = usePluginContext();
    const tabCtx = useTabsContext();

    const marketingEventDP = new MarketingEventsDataProvider(appCtx.coreApiService);
    const user2ContractorDataProvider = new User2ContractorDataProvider(appCtx.coreApiService);

    const [data, setData] = useState<IMarketingEventsViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [viewState, setViewState] = useState<GridStateType>("view");

    const [departments, setDepartments] = useState<IEntitySimpleDTO[]>([]);
    const [isDepartmentsLoading, setIsDepartmentsLoading] = useState<boolean>(true)

    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -7 }), endDate: DateTime.now() } as IDatePeriod;
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [gridFilter, dispatchGridFilter] = useGridFilter(MarketingEventsDefaultGridFilter(datePeriod));
    const [gridFilterContractors] = useGridFilter({
        numberPerPage: 1000,
        pageNumber: 1,
        columnFilters: [],
    });
    const [defaultGridFilter] = useGridFilter(CreateSelectorGridFilter)

    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);

    const baseT = (value: string) => t('directory.marketing.' + value);

    const [selectedDepartments, setSelectedDepartments] = useState<ISelectedItems>();
    const [currentContractorValue, setCurrentContractorValue] = useState<string>('')
    const [currentDistributorValue, setCurrentDistributorValue] = useState<string>('')
    const [currentGoodsValue, setCurrentGoodsValue] = useState<string>('')
    const [currentManufacturerValue, setCurrentManufacturerValue] = useState<string>('')

    const marketingFilters = {
        goodsNameFilter: currentGoodsValue,
        producerNameFilter: currentManufacturerValue,
        contractorNameFilter: currentContractorValue,
        departmentsFilter: selectedDepartments,
        documentType: pluginContext.masterGrid.document
    }
    
    const stateOptions = [
        { value: MarketingEventStateText.Preparation, label: MarketingEventStateText.Preparation },
        { value: MarketingEventStateText.ActiveForParticipants, label: MarketingEventStateText.ActiveForParticipants },
        { value: MarketingEventStateText.CompletedByExecutor, label: MarketingEventStateText.CompletedByExecutor },
        { value: MarketingEventStateText.ResultApprovalWithCustomer, label: MarketingEventStateText.ResultApprovalWithCustomer },
        { value: MarketingEventStateText.ApprovedByCustomer, label: MarketingEventStateText.ApprovedByCustomer },
        { value: MarketingEventStateText.Cancelled, label: MarketingEventStateText.Cancelled }
    ];

    const typeOptions = [
        { value: MarketingEventTypeText.PurchaseByQuantity, label: MarketingEventTypeText.PurchaseByQuantity },
        { value: MarketingEventTypeText.PurchaseByAmount, label: MarketingEventTypeText.PurchaseByAmount },
        { value: MarketingEventTypeText.PurchaseByPackage, label: MarketingEventTypeText.PurchaseByPackage },
        { value: MarketingEventTypeText.SalesByQuantity, label: MarketingEventTypeText.SalesByQuantity },
        { value: MarketingEventTypeText.SalesByAmount, label: MarketingEventTypeText.SalesByAmount },
        { value: MarketingEventTypeText.SalesByPackage, label: MarketingEventTypeText.SalesByPackage },
        { value: MarketingEventTypeText.RemainsMatrix, label: MarketingEventTypeText.RemainsMatrix },
        { value: MarketingEventTypeText.ProductOfTheDay, label: MarketingEventTypeText.ProductOfTheDay },
        { value: MarketingEventTypeText.Display, label: MarketingEventTypeText.Display },
        { value: MarketingEventTypeText.CommercialInput, label: MarketingEventTypeText.CommercialInput }
    ];

    const handleDateFilterChange = useCallback((date: IDatePeriod) => {

        const filtersWithoutDates = gridFilter.columnFilters.filter(
            filter => filter.name !== "dateFrom" && filter.name !== "dateTo"
        );

        const newFilters: IGridColumnFilter[] = [...filtersWithoutDates];
        if (date?.endDate) {
            newFilters.push({
                name: "dateFrom",
                value: date.endDate.toFormat("yyyyMMdd"),
                operator: "LessOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        if (date?.startDate) {
            newFilters.push({
                name: "dateTo",
                value: date.startDate.toFormat("yyyyMMdd"),
                operator: "MoreOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        dispatchGridFilter({
            type: "newColumnFilters",
            payload: newFilters
        });
    }, [dispatchGridFilter, gridFilter.columnFilters]);

    const handleDatePeriodChange = useCallback((date: IDatePeriod) => {
        setDatePeriod(date);
        handleDateFilterChange(date);
    }, [handleDateFilterChange]);

    const handleSearchDepartmentsIdsChange = (selectedItems: ISelectedItems) => {
        setSelectedDepartments(selectedItems);
    };

    const handleSearchContractorChange = (value: string) => {
        setCurrentContractorValue(value);
    };

    const handleSearchDistributorChange = (value: string) => {
        setCurrentDistributorValue(value);
    };

    const handleSearchGoodsChange = (value: string) => {
        setCurrentGoodsValue(value);
    };

    const handleSearchManufacturerChange = (value: string) => {
        setCurrentManufacturerValue(value);
    };

    function getContractors() {
        const userContractorsFilter = {
            ...gridFilterContractors,
            columnFilters: [
                {
                    name: "idUserGlobal",
                    operator: 'Eq',
                    value: userContext.idGlobal
                }
            ]
        } as IGridFilter;
        setIsDepartmentsLoading(true)
        user2ContractorDataProvider.getView(userContractorsFilter, (values) => {
            const transformedData: IEntitySimpleDTO[] = values.map(item => ({
                idGlobal: item.idContractorGlobal,
                displayName: item.contractorName
            }))
            setDepartments(transformedData)
            setIsDepartmentsLoading(false)
        })
    }

    useEffect(() => {
        getContractors();
    }, [defaultGridFilter])

    useEffect(() => {
        setIsSubmitting(true)

        marketingEventDP.getViewParam(gridFilter, async (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            setLoadingStatus(LoadingStatus.Completed);
            setIsSubmitting(false);
            setSelectedItem(undefined)

        })
    }, [gridFilter])

    useEffect(() => {
        pluginContext.masterGrid.onUnselectEvent();
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (viewState === 'refresh') {
            setIsSubmitting(true);

            marketingEventDP.getViewParam(gridFilter, async (data, totalCount) => {
                setData(data);
                setViewState('view');
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
                setIsSubmitting(false);
            })
        }
    }, [viewState])

    return (
        <PluginWrapper>
            <ToolbarWrapper className={marketingStyles.toolbarWrapper}>
                <DefaultDictionaryCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    dataProvider={marketingEventDP}
                    creatorModalJsx={MarketingEventCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    multipleSelect={[multipleSelect, setMultipleSelect]}
                />
                <DatePeriodPanelV2
                    datePeriod={datePeriod}
                    onDatePeriodChange={handleDatePeriodChange}
                />
                <div className={marketingStyles.filtersContainer}>
                    <SelectAnt
                        placeholder={baseT("allStates")}
                        style={{ width: 200 }}
                        filterName="stateText"
                        gridFilter={gridFilter}
                        dispatchGridFilter={dispatchGridFilter}
                        disabled={isSubmitting || loadingStatus === LoadingStatus.InProcess}
                        loading={isSubmitting}
                        itemOptions={stateOptions}
                    />
                    <SelectAnt
                        placeholder={baseT("allTypes")}
                        style={{ width: 200 }}
                        filterName="typeText"
                        gridFilter={gridFilter}
                        dispatchGridFilter={dispatchGridFilter}
                        disabled={isSubmitting || loadingStatus === LoadingStatus.InProcess}
                        loading={isSubmitting}
                        itemOptions={typeOptions}
                    />
                </div>
            </ToolbarWrapper>
            <Accordion
                opened={false}
                caption={baseT("filters")}
                id={`${uuidv4()}`}
            >
                <FlexRow>
                    <div className={marketingStyles.filtersWrapper}>
                        <EntityMultiSelect
                            loading={isDepartmentsLoading}
                            items={departments}
                            filterName="idContractorGlobalList"
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchDepartmentsIdsChange}
                            placeholder={baseT("departments")}
                            showClearButton={true}
                        />
                        <div className={marketingStyles.filtersWrapper}>
                            <InputSearch
                                searchValue={currentContractorValue}
                                typeFilter={'paramFilter'}
                                gridFilter={gridFilter}
                                dispatchGridFilter={dispatchGridFilter}
                                handleSearchChange={handleSearchContractorChange}
                                filterName="LegalEntityName"
                                placeholder={baseT("contractors")}
                                debounceDelay={1000}
                            />
                            <InputSearch
                                searchValue={currentDistributorValue}
                                typeFilter={'paramFilter'}
                                gridFilter={gridFilter}
                                dispatchGridFilter={dispatchGridFilter}
                                handleSearchChange={handleSearchDistributorChange}
                                filterName="distributorName"
                                placeholder={baseT("distributors")}
                                debounceDelay={1000}
                            />
                        </div>
                    </div>
                </FlexRow>

                <FlexRow>
                    <div className={marketingStyles.filtersWrapper}>
                        <InputSearch
                            searchValue={currentGoodsValue}
                            typeFilter={'paramFilter'}
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchGoodsChange}
                            filterName="goodsName"
                            placeholder={baseT("goods")}
                            debounceDelay={1000}
                        />
                        <InputSearch
                            searchValue={currentManufacturerValue}
                            typeFilter={'paramFilter'}
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchManufacturerChange}
                            filterName="producerName"
                            placeholder={baseT("manufacturer")}
                            debounceDelay={1000}
                        />
                    </div>
                </FlexRow>
            </Accordion>
            {
                isSubmitting ? <div className={marketingStyles.loading}><Spinner /></div>
                    :
                    <div className={styles.gridWrapper}>
                        <div className={styles.masterGrid}>
                            <DefaultGrid
                                searching
                                isServerFilter
                                showQuantity
                                gridId={props.gridId}
                                data={data}
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                separator
                                singleDirectory
                                totalCount={totalCount}
                                filter={gridFilter}
                                dataProvider={marketingEventDP}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                multipleSelect={multipleSelect}
                                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                getView={(gridFilter, callback) => {
                                    marketingEventDP.getViewParam(gridFilter, (entities) => {
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row)
                                    props.onSelect?.(row)
                                    row
                                        ? pluginContext.masterGrid.onSelectEvent(row, DocumentType.marketingEvents)
                                        : pluginContext.masterGrid.onUnselectEvent()
                                }}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows)
                                    props?.onMultipleSelect?.(rows)
                                }}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                onEnter={() => setViewState("edit")}
                                getStyleRow={(item) => {
                                    const cell = item.cells.find(x => x.propertyName === 'percentageOfCompletion');
                                    if (cell?.value > 100) return styles.rowGreen;
                                    if (cell?.value <= 100 && cell?.value > 80) return styles.rowYellow;
                                    if (cell?.value <= 80 && cell?.value > 60) return styles.rowOrange;
                                    return null;
                                }}
                            />
                        </div>
                        <div className={styles.detailsTabView}>
                            {
                                detailsTabsPanel.tabs.length > 0 &&
                                selectedItem &&
                                !multipleSelect &&
                                (
                                    <div>
                                        <div className={tabsStyles.tabsPanelWrapper}>
                                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: 'hidden', width: '100%' }}>
                                                <TabsPanel
                                                    id="detailsTabsPanel"
                                                    activeId={detailsTabsPanel.currentTab?.id}
                                                    tabs={detailsTabsPanel.tabs}
                                                    onActive={(tab) => {
                                                        dispatchDetailsTabsPanel({ type: "activate", payload: tab })
                                                    }}>
                                                </TabsPanel>
                                            </div>
                                        </div>
                                        <div className={tabsStyles.contentWrapper}>
                                            {detailsTabsPanel.tabs.map(item => {
                                                const isActive = item.id === detailsTabsPanel.currentTab?.id;
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className={classNames(isActive ? tabsStyles.contentVisible : tabsStyles.contentHidden)}
                                                    >
                                                        {isActive && React.isValidElement(item.view.content) ? (
                                                            React.cloneElement(item.view.content, {
                                                                key: `${item.id}_${currentGoodsValue}_${currentManufacturerValue}_${currentContractorValue}_${selectedDepartments?.ids[0]}`,
                                                                marketingFilters: marketingFilters
                                                            } as any)
                                                        ) : (
                                                            item.view.content
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
            }
        </PluginWrapper>
    )
}